import { FC, useMemo, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { FormLabel } from '@/components/ui/form';
import { DatePicker } from 'src/components/DatePicker';
import { AddressForm, AddressZ } from 'src/components/address/AddressForm';
import { Button } from '@/components/ui/button';
import { LuClock } from 'react-icons/lu';
import { ArrowRight, Building2, Edit, Info, X } from 'lucide-react';
import {
  MatchableAddress,
  findMatchingSavedAddress,
  formatAddressLabel,
  isAddressChanged,
  isNewUnitOnKnownStreet,
} from '../address-matching';

interface Address {
  _id?: string;
  googlePlacesId?: string;
  line1?: string;
  line2?: string | null;
  city?: string;
  state?: string;
  postalCode?: string;
  type?: 'delivery' | 'billing';
}

interface OrderDeliveryFormData {
  dropOffWindow: {
    day: Date;
    range: string;
  };
  pickupWindow: {
    day: Date;
    range: string;
  };
  restrictions: Record<string, string>;
  placementInstructions?: string;
}

interface AddressAndScheduleTabProps {
  control: Control<OrderDeliveryFormData>;
  userId?: string;
  address?: Address;
  onAddressFormChange?: (address: AddressZ, placeId: string) => void;
  /** The address currently staged in the form, if the admin has touched it. */
  pendingAddress?: { address: AddressZ; placeId: string };
  /** The customer's other saved addresses, used to explain what saving will do. */
  savedAddresses?: MatchableAddress[];
}

export const AddressAndScheduleTab: FC<AddressAndScheduleTabProps> = ({
  control,
  userId,
  address,
  onAddressFormChange,
  pendingAddress,
  savedAddresses,
}) => {
  const [isEditingAddress, setIsEditingAddress] = useState(false);

  const proposed = useMemo<MatchableAddress | null>(() => {
    if (!pendingAddress?.address?.line1) {
      return null;
    }
    return {
      line1: pendingAddress.address.line1,
      line2: pendingAddress.address.line2,
      city: pendingAddress.address.city,
      state: pendingAddress.address.state,
      postalCode: pendingAddress.address.postalCode,
      googlePlacesId: pendingAddress.placeId || undefined,
    };
  }, [pendingAddress]);

  const hasChanged = isAddressChanged(proposed, address);
  const matchedSavedAddress = useMemo(
    () => findMatchingSavedAddress(proposed, savedAddresses),
    [proposed, savedAddresses],
  );
  const showsNewUnitWarning = useMemo(
    () => isNewUnitOnKnownStreet(proposed, savedAddresses),
    [proposed, savedAddresses],
  );

  return (
    <div className="space-y-6">
      {/* Address Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-gray-900">Delivery Address</h4>
          {!isEditingAddress && address && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsEditingAddress(true)}
              className="text-xs"
            >
              <Edit className="h-3 w-3 mr-1" />
              Edit Address
            </Button>
          )}
        </div>

        {isEditingAddress || !address ? (
          <div className="card bg-slate-50 p-4 w-full relative">
            {isEditingAddress && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingAddress(false)}
                className="absolute top-2 right-2 h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            {userId && (
              <div className="w-full px-2 pb-2 pt-6">
                <AddressForm
                  addressType="delivery"
                  userId={userId}
                  existingAddress={
                    address
                      ? ({
                          ...address,
                          type: address.type || 'delivery',
                        } as AddressZ)
                      : undefined
                  }
                  googlePlacesId={address?.googlePlacesId}
                  showSaveButton={false}
                  onFormChange={onAddressFormChange}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-900">{address.line1}</p>
            {address.line2 && <p className="text-xs text-gray-500 mt-1">{address.line2}</p>}
            <p className="text-xs text-gray-500 mt-1">
              {address.city}, {address.state} {address.postalCode}
            </p>
          </div>
        )}

        {/* Confirm the change before it is saved, and say what will happen to the saved
            address - reused, or saved as a separate record. */}
        {proposed && hasChanged && (
          <div
            data-testid="address-change-confirmation"
            className="card bg-slate-50 p-4 mt-4 flex flex-col items-stretch justify-start gap-4"
          >
            <h4 className="text-sm font-semibold text-gray-900">Confirm the change</h4>

            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <label className="label text-xs">From</label>
                <p className="text-sm font-medium text-text-primary break-words">
                  {formatAddressLabel(address)}
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted mt-6 shrink-0" />
              <div className="flex-1 min-w-0">
                <label className="label text-xs">To</label>
                <p className="text-sm font-medium text-text-primary break-words">
                  {formatAddressLabel(proposed)}
                </p>
              </div>
            </div>

            {matchedSavedAddress && (
              <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-white p-3">
                <Building2 className="h-4 w-4 text-muted mt-0.5 shrink-0" />
                <p className="text-xs text-text-primary">
                  This matches an address already saved for this customer. The order will point at
                  that saved address — nothing else about it changes.
                </p>
              </div>
            )}

            {showsNewUnitWarning && (
              <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-white p-3">
                <Info className="h-4 w-4 text-muted mt-0.5 shrink-0" />
                <p className="text-xs text-text-primary">
                  This customer already has a saved address on this street with a different unit. A
                  separate address will be saved for this order, so the existing one stays as it is
                  for any other orders using it.
                </p>
              </div>
            )}

            <p className="text-xs text-muted">
              Line 2 is part of the saved address. A different unit is saved as its own address.
            </p>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Drop-off Schedule */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-4">Drop-off Schedule</h4>
        <div className="grid grid-cols-2 gap-4">
          {/* Time Display */}
          <div>
            <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">Time</FormLabel>
            <div
              data-testid="dropoff-time-display"
              className="flex flex-row gap-2 items-center p-3 border rounded-md bg-muted/10 h-10"
            >
              <LuClock className="w-5 h-5 text-muted" />
              <span className="text-sm">6:00 AM - 6:00 PM</span>
            </div>
          </div>

          {/* Date Picker */}
          <Controller
            control={control}
            name="dropOffWindow.day"
            render={({ field }) => (
              <div>
                <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">Date</FormLabel>
                <DatePicker
                  {...field}
                  date={field.value ? new Date(field.value) : new Date()}
                  setDate={field.onChange}
                />
              </div>
            )}
          />
        </div>
      </div>

      {/* Pickup Schedule */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-4">Pickup Schedule</h4>
        <div className="grid grid-cols-2 gap-4">
          {/* Time Display */}
          <div>
            <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">Time</FormLabel>
            <div
              data-testid="pickup-time-display"
              className="flex flex-row gap-2 items-center p-3 border rounded-md bg-muted/10 h-10"
            >
              <LuClock className="w-5 h-5 text-muted" />
              <span className="text-sm">6:00 AM - 6:00 PM</span>
            </div>
          </div>

          {/* Date Picker */}
          <Controller
            control={control}
            name="pickupWindow.day"
            render={({ field }) => (
              <div>
                <FormLabel className="text-sm font-medium text-gray-700 mb-2 block">Date</FormLabel>
                <DatePicker
                  {...field}
                  date={field.value ? new Date(field.value) : new Date()}
                  setDate={field.onChange}
                />
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
};
